import { isSlackPermalink } from './utils';

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:']);

function safeLink(value: string): string | undefined {
  try {
    const url = new URL(value);
    return SAFE_LINK_PROTOCOLS.has(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function appendInline(parent: Node, source: string, depth = 0): void {
  if (!source || depth > 8) {
    parent.appendChild(document.createTextNode(source));
    return;
  }
  const token = /(`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]+\]\([^\s)]+\))/g;
  let cursor = 0;
  for (const match of source.matchAll(token)) {
    const index = match.index ?? 0;
    if (index > cursor) parent.appendChild(document.createTextNode(source.slice(cursor, index)));
    const value = match[0];
    if (value.startsWith('`')) {
      const code = document.createElement('code');
      code.textContent = value.slice(1, -1);
      parent.appendChild(code);
    } else if (value.startsWith('**') || value.startsWith('__')) {
      const strong = document.createElement('strong');
      appendInline(strong, value.slice(2, -2), depth + 1);
      parent.appendChild(strong);
    } else if (value.startsWith('*') || value.startsWith('_')) {
      const emphasis = document.createElement('em');
      appendInline(emphasis, value.slice(1, -1), depth + 1);
      parent.appendChild(emphasis);
    } else {
      const link = value.match(/^\[([^\]]+)\]\(([^\s)]+)\)$/);
      const href = link ? safeLink(link[2]) : undefined;
      if (!link || !href) {
        parent.appendChild(document.createTextNode(value));
      } else {
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.className = isSlackPermalink(href)
          ? 'cm-markdown-link cm-slack-link'
          : 'cm-markdown-link';
        appendInline(anchor, link[1], depth + 1);
        parent.appendChild(anchor);
      }
    }
    cursor = index + value.length;
  }
  if (cursor < source.length) parent.appendChild(document.createTextNode(source.slice(cursor)));
}

function startsBlock(line: string): boolean {
  return /^(?:#{1,4}\s+|```|>\s?|[-*+]\s+|\d+[.)]\s+|(?:---+|___+|\*\*\*+)\s*$)/.test(
    line.trim(),
  );
}

export function renderMarkdown(markdown: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();
    if (!line) {
      index += 1;
      continue;
    }

    const fence = line.match(/^```\s*([A-Za-z0-9_-]{0,40})\s*$/);
    if (fence) {
      const content: string[] = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index].trim())) {
        content.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      const pre = document.createElement('pre');
      const code = document.createElement('code');
      if (fence[1]) code.className = `language-${fence[1]}`;
      code.textContent = content.join('\n');
      pre.appendChild(code);
      fragment.appendChild(pre);
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const element = document.createElement(`h${heading[1].length}`);
      appendInline(element, heading[2]);
      fragment.appendChild(element);
      index += 1;
      continue;
    }

    if (/^(?:---+|___+|\*\*\*+)\s*$/.test(line)) {
      fragment.appendChild(document.createElement('hr'));
      index += 1;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote = document.createElement('blockquote');
      const quoted: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        quoted.push(lines[index].trim().replace(/^>\s?/, ''));
        index += 1;
      }
      appendInline(quote, quoted.join(' '));
      fragment.appendChild(quote);
      continue;
    }

    const listMatch = line.match(/^([-*+]|\d+[.)])\s+(.+)$/);
    if (listMatch) {
      const ordered = /^\d/.test(listMatch[1]);
      const list = document.createElement(ordered ? 'ol' : 'ul');
      while (index < lines.length) {
        const item = lines[index].trim().match(/^([-*+]|\d+[.)])\s+(.+)$/);
        if (!item || /^\d/.test(item[1]) !== ordered) break;
        const element = document.createElement('li');
        appendInline(element, item[2]);
        list.appendChild(element);
        index += 1;
      }
      fragment.appendChild(list);
      continue;
    }

    const paragraph: string[] = [line];
    index += 1;
    while (index < lines.length && lines[index].trim() && !startsBlock(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    const element = document.createElement('p');
    appendInline(element, paragraph.join(' '));
    fragment.appendChild(element);
  }

  return fragment;
}
